import { AccordionDetails, Box, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import StyledAccordionContainer from '../../../components/Accordions/StyledAccordionContainer'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import BoxAlert from '../../../components/Alerts/BoxAlert'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import { AccordionHeader } from '../../../components/Headers/styles'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { useOrderFormContext } from '../context/OrderFormContext'
import messages from './messages'
import PackagingForm from './PackagingForm'
import { IShipmentFormInput } from './schema'
import ShipFromDeliverToContainer from './ShipFromDeliverToContainer'
import ShipmentDiagram from './ShipmentDiagram'
import ShipmentInformation from './ShipmentInformation'
import { validateShipmentData, ValidationError } from './utils/crossValidation'

type ShipmentSummaryProps = {
  data: IShipmentFormInput
}

const latestStatus = (statuses: any[]) => {
  return statuses
    .filter(status => status.orderStatusTypeId !== 21)
    .reduce((latest, curr) =>
      new Date(curr.statusDate) > new Date(latest.statusDate) ? curr : latest);
}

const ShipmentSummary: React.FC<ShipmentSummaryProps> = ({ data }) => {
  const { formatMessage } = useIntl()
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const { setCurrentPage } = useOrderFormContext()
  useEffect(() => {
    setCurrentPage(5)
  }, [])

  useEffect(() => {
    if (latestStatus(data.statuses || []).orderStatusTypeId < 4) {
      const validateData = async () => {
        const errors = validateShipmentData(data)
        setValidationErrors(errors)
      }
      validateData()
    }
  }, [data])

  return (
    <Box>
      <ConditionalRender condition={validationErrors.length > 0}>
        <Box sx={{ mb: 1 }}>
          {validationErrors.map((error) => (
            <BoxAlert
              key={error.id}
              severity={error.severity}
              message={error.message}
              boxProps={{
                sx: {
                  m: 0,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '40px',
                },
              }}
            />
          ))}
        </Box>
      </ConditionalRender>
      <ShipmentInformation />
      {data.bookings &&
        data.bookings.map((consignment: any, cIndex: number) => (
          <StyledAccordionContainer
            key={cIndex}
            sx={(theme) => ({ backgroundColor: theme.palette?.accordion?.parent?.bg, mb: '16px' })}
          >
            <StyledAccordionSummary index={`consignment-summary-${cIndex}`}>
              <DefaultSpacingGrid container spacing={0}>
                <Grid item xs={12} md={3}>
                  <AccordionHeader>
                    {formatMessage(messages.consignmentItem, { idx: cIndex + 1 })}
                  </AccordionHeader>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ml: '-16px', mt: '2px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.consignmentTrackingNumber)}
                    value={consignment.trackingNumber || 'N/A'}
                    isSmallScreen={false}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ml: '4px', mt: '2px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.shipFrom)}
                    value={consignment.shipFrom?.location?.name || 'N/A'}
                    isSmallScreen={false}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ml: '8px', mt: '2px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.deliverTo)}
                    value={consignment.deliverTo?.location?.name || 'N/A'}
                    isSmallScreen={false}
                  />
                </Grid>
              </DefaultSpacingGrid>
            </StyledAccordionSummary>
            <AccordionDetails>
              <ShipFromDeliverToContainer consignmentIndex={cIndex} disabled={true} />
              <PackagingForm consignmentIndex={cIndex} disabled={true} variant='summary' />
            </AccordionDetails>
          </StyledAccordionContainer>
        ))}
      <ShipmentDiagram shipmentData={data as IShipmentFormInput} />
    </Box>
  )
}

export default ShipmentSummary
